import React, { useState } from 'react';
import { customersAPI } from '../services/api';
import './CSVUpload.css';

const CSVUpload = ({ onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await customersAPI.uploadCSV(formData);
      setResult(response.data);
      setFile(null);
      if (onSuccess) onSuccess();
      
      // Reset file input
      document.getElementById('csv-file').value = '';
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="csv-upload">
      <div className="card">
        <h2>Upload Customer Data</h2>
        <p className="description">
          Upload a CSV file containing customer order data. The file should include the following columns:
        </p>

        <div className="csv-format">
          <h3>Required CSV Format (Payment Gateway Export):</h3>
          <p>This system accepts CSV exports from payment gateways (Stripe, WIX Payments, etc.)</p>
          <ul>
            <li><strong>id</strong> - Payment/transaction ID (required)</li>
            <li><strong>Created date (UTC)</strong> - Order date and time (required)</li>
            <li><strong>Amount</strong> - Payment amount (required, numeric)</li>
            <li><strong>Customer Name (metadata)</strong> - Customer name (required)</li>
            <li><strong>Customer Email (metadata)</strong> - Email address (optional)</li>
            <li><strong>Customer Phone (metadata)</strong> - Phone number (optional)</li>
            <li><strong>Order Item #1-11 (metadata)</strong> - Order items (optional)</li>
          </ul>

          <div className="example-csv">
            <h4>Accepted CSV Formats:</h4>
            <p>✅ Stripe payment exports</p>
            <p>✅ WIX payment exports</p>
            <p>✅ Any payment gateway CSV with customer and transaction data</p>
            <p className="note">The system automatically extracts customer information from metadata columns</p>
          </div>
        </div>

        <form onSubmit={handleUpload} className="upload-form">
          <div className="file-input-wrapper">
            <input
              type="file"
              id="csv-file"
              accept=".csv"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="csv-file" className="file-label">
              {file ? file.name : 'Choose CSV file...'}
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </button>
        </form>

        {error && <div className="error-message">{error}</div>}

        {result && (
          <div className="success-message">
            <h3>✅ Upload Complete!</h3>
            <div className="upload-stats">
              <p><strong>📊 Total Rows Processed:</strong> {result.totalRows || (result.processedCount + result.skippedCount + result.errorCount) || 0}</p>
              <p><strong>✓ New Orders Added:</strong> {result.processedCount || result.imported || 0}</p>
              {(result.skippedCount > 0 || result.skipped > 0) && (
                <p className="info-text"><strong>⏭️  Duplicates Skipped:</strong> {result.skippedCount || result.skipped} (already in database - no duplicates created)</p>
              )}
              {result.errorCount > 0 && (
                <p className="error-text"><strong>❌ Errors:</strong> {result.errorCount} rows could not be processed</p>
              )}
            </div>
            {result.errorDetails && result.errorDetails.length > 0 && (
              <div className="upload-errors">
                <h4>Error Details:</h4>
                <ul>
                  {result.errorDetails.slice(0, 10).map((err, idx) => (
                    <li key={idx}>
                      Payment ID {err.paymentId || 'N/A'}: {err.error}
                    </li>
                  ))}
                  {result.errorDetails.length > 10 && (
                    <li>... and {result.errorDetails.length - 10} more errors</li>
                  )}
                  {result.truncated && (
                    <li className="warning-text">⚠️ Error list truncated at 100 entries</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <h3>How It Works</h3>
        <ol className="instructions">
          <li>Export your payment data from your payment gateway (Stripe, WIX, etc.)</li>
          <li>Click "Choose CSV file" and select the exported CSV file</li>
          <li>Click "Upload CSV" to import the data</li>
          <li>The system will automatically:
            <ul>
              <li>Extract customer information from metadata columns</li>
              <li>Match existing customers by email or phone</li>
              <li>Create new customer records when needed</li>
              <li>Aggregate total orders and spending per customer</li>
              <li>Skip duplicate payments (same payment ID)</li>
            </ul>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default CSVUpload;
